import {
  CharityVettingUrlType,
  DonationPreferencesEnum,
  type CharityVettingFormValues,
  type CharityVettingUrlTypeValues,
} from 'features/charity-vetting/types/charityVettingTypes';
import { DocumentType, type DocumentTypeValue } from 'features/documents/types';

type CharityOnboarding = Partial<CharityVettingFormValues['charityOnboarding']>;

export type VettingDocumentConfig = {
  segment: CharityVettingUrlTypeValues;
  documentType: DocumentTypeValue;
  isMandatory: boolean;
  appliesTo: (charityOnboarding: CharityOnboarding) => boolean;
};

export const acceptsFood = (charityOnboarding: CharityOnboarding): boolean =>
  charityOnboarding.donationPreferences === DonationPreferencesEnum.ONLY_FOOD ||
  charityOnboarding.donationPreferences === DonationPreferencesEnum.FOOD_AND_NON_FOOD;

// `LIABILITY_INSURANCE` must stay last: both chains end on it, and only `urlTypeId.at(-1)` marks the section complete.
export const FR_DOCUMENTS: VettingDocumentConfig[] = [
  {
    segment: CharityVettingUrlType.ASSOCIATION_REGISTRATION,
    documentType: DocumentType.AssociationRegistration,
    isMandatory: true,
    appliesTo: () => true,
  },
  {
    segment: CharityVettingUrlType.FOOD_AID_HABILITATION,
    documentType: DocumentType.FoodAidHabilitation,
    isMandatory: true,
    appliesTo: (charityOnboarding) =>
      acceptsFood(charityOnboarding) && charityOnboarding.isAuthorisedFoodAid === true,
  },
  {
    segment: CharityVettingUrlType.FOOD_ESTABLISHMENT_DECLARATION,
    documentType: DocumentType.FoodEstablishmentDeclaration,
    isMandatory: true,
    appliesTo: (charityOnboarding) =>
      acceptsFood(charityOnboarding) && charityOnboarding.isAuthorisedFoodAid !== true,
  },
  {
    segment: CharityVettingUrlType.FOOD_SAFETY_TRAINING_CERTIFICATE,
    documentType: DocumentType.FoodSafetyTrainingCertificate,
    isMandatory: true,
    appliesTo: acceptsFood,
  },
  {
    segment: CharityVettingUrlType.LIABILITY_INSURANCE,
    documentType: DocumentType.LiabilityInsurance,
    isMandatory: true,
    appliesTo: () => true,
  },
];

export const applicableDocuments = (
  charityOnboarding: CharityOnboarding,
): VettingDocumentConfig[] => FR_DOCUMENTS.filter(({ appliesTo }) => appliesTo(charityOnboarding));

export const documentBySegment = (segment: string | undefined): VettingDocumentConfig | undefined =>
  FR_DOCUMENTS.find((document) => document.segment === segment);
